"use client";

import { useId, useState, type ReactNode } from "react";
import { useTranslations } from "use-intl";

import { AdminOfferEmitButton } from "@/components/admin-offer-emit-button";
import { BestDealOfferEditionRow } from "@/components/marketplace/best-deal-offer-edition-row";
import { bestDealOfferHeading } from "@/components/marketplace/best-deal-offer-heading";
import { BestDealOfferOtherEditionNotice } from "@/components/marketplace/best-deal-offer-other-edition-notice";
import { BestDealOfferPriceBlock } from "@/components/marketplace/best-deal-offer-price-block";
import { currentFormatOfferCount } from "@/components/marketplace/current-format-offer-count";
import { DealOfferCheckedAt } from "@/components/marketplace/deal-offer-checked-at";
import { DealOfferHistoryButton } from "@/components/marketplace/deal-offer-history-button";
import { DealOfferPanel } from "@/components/marketplace/deal-offer-panel";
import { DealOfferPanelFooter } from "@/components/marketplace/deal-offer-panel-footer";
import { OfferPriceHistory } from "@/components/marketplace/deal-offer-price-history-modal";
import type { DealOffer } from "@/components/marketplace/deal-offers-types";
import { effectiveDealPrice } from "@/components/marketplace/deal-offers-utils";
import { OfferPriceRangeScale } from "@/components/marketplace/offer-price-range-scale";
import type { FormatCategoryWithOther } from "@garimpa/lib/formatGroups";

function offerMatchesContextRelease(offer: DealOffer, contextReleaseId?: string) {
  if (!contextReleaseId) return true;
  return offer.matchedReleaseIds.some((releaseId) => String(releaseId) === contextReleaseId);
}

function offerSiblingFormatLabel(
  offer: DealOffer,
  tFormats: ReturnType<typeof useTranslations<"shared.releaseFilters">>,
) {
  if (!offer.formatCategory) return tFormats("other");
  return tFormats.has(offer.formatCategory) ? tFormats(offer.formatCategory) : offer.formatCategory;
}

function BestDealOfferRangeScale({ offer }: { offer: DealOffer }) {
  const t = useTranslations("shared.affiliates");
  return (
    <div className="flex flex-col">
      <OfferPriceRangeScale
        className="rounded-none bg-transparent px-0 py-0"
        price={{
          currentPrice: effectiveDealPrice(offer),
          dealReferencePrice: offer.dealReferencePrice,
        }}
        displayPrice={offer.currentPrice}
        lowestPrice={offer.priceHistoryMin}
        averagePrice={offer.priceHistoryAverage}
        highestPrice={offer.priceHistoryMax}
        currency={offer.priceHistoryCurrency ?? offer.currentCurrency}
        movementSource={offer}
        priceIndicators={offer.priceIndicators}
        latestLabel={t("itemPrice")}
        showPriceSummary={false}
        showReferencePrice={false}
        inlineRangeLabels
        subtleRangeBar
        streamlinedSignal
      />
    </div>
  );
}

function BestDealOfferInsights({
  currency,
  historyExpanded,
  historyRegionId,
  offer,
  onToggleHistory,
}: {
  currency: string;
  historyExpanded: boolean;
  historyRegionId: string;
  offer: DealOffer;
  onToggleHistory: () => void;
}) {
  const t = useTranslations("shared.affiliates");
  if (!offer.priceInsightsVisible) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center justify-between gap-x-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {t("offerHistoryCaption", { scope: offer.listingAnalysis ? "listing" : "offer" })}
        </p>
        <DealOfferHistoryButton
          variant="link"
          expanded={historyExpanded}
          controls={historyExpanded ? historyRegionId : undefined}
          onClick={onToggleHistory}
        />
      </div>
      {historyExpanded ? (
        <div id={historyRegionId}>
          <OfferPriceHistory offer={offer} currency={currency} />
        </div>
      ) : (
        <BestDealOfferRangeScale offer={offer} />
      )}
    </div>
  );
}

function BestDealOfferContext({
  headingLabel,
  isOtherEdition,
  offer,
  presentationScope,
}: {
  headingLabel: string;
  isOtherEdition: boolean;
  offer: DealOffer;
  presentationScope: "release" | "work";
}) {
  if (presentationScope === "work") {
    return <BestDealOfferEditionRow label={headingLabel} offer={offer} />;
  }
  if (isOtherEdition) return <BestDealOfferOtherEditionNotice offer={offer} />;
  return null;
}

function useBestDealOfferHeading(input: {
  contextReleaseId?: string;
  matchesContextRelease: boolean;
  offer: DealOffer;
  presentationScope: "release" | "work";
}) {
  const t = useTranslations("shared.affiliates");
  const tFormats = useTranslations("shared.releaseFilters");
  return bestDealOfferHeading({
    presentationScope: input.presentationScope,
    matchesContextRelease: input.matchesContextRelease,
    hasContextRelease: Boolean(input.contextReleaseId),
    siblingFormat: offerSiblingFormatLabel(input.offer, tFormats),
    offerForThisEdition: t("offerForThisEdition"),
    bestOffer: t("bestOffer"),
    otherEdition: t("otherEdition"),
    lowestCurrentPriceAcrossEditions: t("lowestCurrentPriceAcrossEditions"),
  });
}

export function BestDealOffer({
  compareFormat,
  compareOfferCount,
  offer,
  contextReleaseId,
  offers,
  currency,
  formatSwitch,
  presentationScope,
  onExplore,
  allEditions,
  className,
}: {
  compareFormat?: FormatCategoryWithOther;
  compareOfferCount?: number;
  offer: DealOffer;
  contextReleaseId?: string;
  offers: DealOffer[];
  currency: string;
  /**
   * Work scope: the segmented offer-format switch. It takes the heading row, and the heading
   * label moves to the edition row.
   */
  formatSwitch?: ReactNode;
  presentationScope: "release" | "work";
  onExplore: (format?: FormatCategoryWithOther) => void;
  allEditions?: boolean;
  className?: string;
}) {
  const [historyExpanded, setHistoryExpanded] = useState(false);
  const historyRegionId = useId();
  const matchesContextRelease = offerMatchesContextRelease(offer, contextReleaseId);
  const headingLabel = useBestDealOfferHeading({
    contextReleaseId,
    matchesContextRelease,
    offer,
    presentationScope,
  });
  const offerCount = allEditions
    ? offers.length
    : currentFormatOfferCount({
        compareOfferCount,
        offers,
        formatCategory: offer.formatCategory,
      });
  const isOtherEdition = presentationScope === "release" && !matchesContextRelease;
  const hasMultipleOffers = offerCount > 1;

  return (
    <DealOfferPanel
      className={className}
      heading={headingLabel}
      headingControl={presentationScope === "work" ? (formatSwitch ?? null) : undefined}
      headingMeta={
        <div className="ml-auto flex items-center gap-2">
          <DealOfferCheckedAt lastCheckedAt={offer.lastCheckedAt} />
          <AdminOfferEmitButton
            offerId={offer.offerId}
            offerTitle={offer.title}
            providerName={offer.providerDisplayName}
            className="size-9"
          />
        </div>
      }
      footer={
        <DealOfferPanelFooter
          actions={
            hasMultipleOffers ? (
              <DealOfferHistoryButton
                variant="link"
                mode="offers"
                count={offerCount - 1}
                onClick={() => onExplore(compareFormat ?? offer.formatCategory ?? undefined)}
              />
            ) : undefined
          }
        />
      }
    >
      {presentationScope === "release" && formatSwitch}
      <BestDealOfferContext
        headingLabel={headingLabel}
        isOtherEdition={isOtherEdition}
        offer={offer}
        presentationScope={presentationScope}
      />
      <BestDealOfferPriceBlock
        offer={offer}
        showTitle={presentationScope === "release" && !isOtherEdition}
      />
      <BestDealOfferInsights
        currency={currency}
        historyExpanded={historyExpanded}
        historyRegionId={historyRegionId}
        offer={offer}
        onToggleHistory={() => setHistoryExpanded((current) => !current)}
      />
    </DealOfferPanel>
  );
}
